import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

// Helper function to resolve wildcard values to actual IDs for display
const resolveWildcardForDisplay = (filterValue, allItems) => {
  if (filterValue === '*') {
    return allItems.map((item) => +item.id);
  }
  if (Array.isArray(filterValue)) {
    return filterValue;
  }
  return [];
};

// Helper function to check if filter should be applied (has selections or wildcard)
const shouldApplyFilter = (filterValue) => {
  return filterValue === '*' || (Array.isArray(filterValue) && filterValue.length > 0);
};

// Helper function to check if item matches filter (including wildcard)
// Uses AND logic: item must have ALL selected filter values
const matchesFilter = (filterValue, itemIds) => {
  if (filterValue === '*') {
    return true; // Wildcard matches everything
  }
  if (Array.isArray(filterValue) && filterValue.length > 0) {
    return filterValue.every((filterId) => itemIds.includes(filterId));
  }
  return true; // No filter means no restriction
};

export const fetchNotifications = createAsyncThunk('notifications/fetchNotifications', async () => {
  try {
    const token = Cookies.get('token');

    const [notificationResponse, userSavedNotificationResponse] = await Promise.all([
      fetch(process.env.REACT_APP_BASE_URL + 'notifications/relations', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
      fetch(process.env.REACT_APP_BASE_URL + 'notifications/user-notification-preferences', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    ]);

    const notifications = await notificationResponse.json();
    const notificationsUser = await userSavedNotificationResponse.json();

    const isNotificationEmpty =
      notificationsUser.machines.length === 0 &&
      notificationsUser.machineGroups.length === 0 &&
      notificationsUser.locations.length === 0 &&
      notificationsUser.maintenanceTypes.length === 0;

    const selectedFilters = {
      machines: isNotificationEmpty
        ? [] // Empty when no saved settings
        : notificationsUser.machines.map((machine) => +machine.machine.id),
      machineGroups: isNotificationEmpty
        ? [] // Empty when no saved settings
        : notificationsUser.machineGroups.map((group) => +group.machine_group.id),
      locations: isNotificationEmpty
        ? [] // Empty when no saved settings
        : notificationsUser.locations.map((location) => +location.location.id),
      maintenanceTypes: isNotificationEmpty
        ? [] // Empty when no saved settings
        : notificationsUser.maintenanceTypes.map((type) => +type.maintenance_type.id),
    };

    // Parse filterStructure if available
    let filterGroups = [];
    let useAdvancedFilters = false;

    if (notificationsUser.notify?.filter_structure) {
      try {
        filterGroups = JSON.parse(notificationsUser.notify.filter_structure);
        useAdvancedFilters = true;
      } catch (error) {
        console.error('Failed to parse filter_structure:', error);
        filterGroups = [];
        useAdvancedFilters = false;
      }
    }

    return {
      filters: {
        machines: notifications.machines.map((machine) => ({
          id: +machine.id,
          name: `${machine.name} ${machine.number}`,
        })),
        machineGroups: notifications.machineGroups.map((group) => ({
          id: +group.id,
          name: group.name,
        })),
        locations: notifications.locations.map((location) => ({
          id: +location.id,
          name: location.name,
        })),
        maintenanceTypes: notifications.maintenanceTypes.map((type) => ({
          id: +type.id,
          name: type.name,
        })),
      },
      selectedFilters,
      rawMachines: notifications.machines.map((machine) => ({
        ...machine,
        id: +machine.id,
        locationIds: (machine.locationIds || []).map(Number),
        machineGroupIds: (machine.machineGroupIds || []).map(Number),
        machineIds: (machine.machineIds || []).map(Number),
        maintenanceTypeIds: (machine.maintenanceTypeIds || []).map(Number),
      })),
      enabled: notificationsUser.notify?.enabled ?? true,
      filterGroups,
      useAdvancedFilters,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const deleteNotifications = createAsyncThunk(
  'notifications/deleteNotifications',
  async () => {
    try {
      const token = Cookies.get('token');

      const response = await fetch(
        process.env.REACT_APP_BASE_URL + 'notifications/user-notification-preferences',
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        console.error('deleteNotifications API error:', response.status, errorData);
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }

      // Handle empty response
      const responseText = await response.text();
      if (!responseText) {
        return { success: true, status: response.status };
      }

      try {
        return JSON.parse(responseText);
      } catch (error) {
        return { success: true, status: response.status, message: responseText };
      }
    } catch (error) {
      console.log('deleteNotifications error:', error);
      throw error;
    }
  }
);

export const saveNotifications = createAsyncThunk(
  'notifications/saveNotifications',
  async (_, { getState }) => {
    try {
      const state = getState().notifications;
      const data = state.selectedFilters;
      const enabled = state.enabled ?? true; // Fallback to true if undefined
      const useAdvanced = state.useAdvancedFilters;
      const filterGroups = state.filterGroups;
      const globalOperator = state.globalOperator || 'AND';

      const requestBody = {
        ...data,
        enabled,
      };

      // Add advanced filters as structured object (preferred by backend)
      if (useAdvanced && filterGroups.length > 0) {
        const advancedFiltersObject = {
          globalOperator,
          groups: filterGroups,
        };

        // Validate size before sending
        const filterStructure = JSON.stringify(advancedFiltersObject);
        const sizeInBytes = new Blob([filterStructure]).size;
        if (sizeInBytes > 60000) { // 60KB safety margin
          throw new Error('Filter structure is too large. Please reduce the number of filter groups or conditions.');
        }

        // Send as structured object (backend will handle it properly)
        requestBody.advancedFilters = advancedFiltersObject;
      }

      const bodyString = JSON.stringify(requestBody);

      const token = Cookies.get('token');
      const response = await fetch(process.env.REACT_APP_BASE_URL + `notifications`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: bodyString,
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('saveNotifications API error:', response.status, errorData);
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }

      // Handle empty response (201 Created with no body)
      const responseText = await response.text();
      if (!responseText) {
        return { success: true, status: response.status };
      }

      try {
        return JSON.parse(responseText);
      } catch (error) {
        console.warn('saveNotifications - response is not JSON:', responseText);
        return { success: true, status: response.status, message: responseText };
      }
    } catch (error) {
      throw error;
    }
  }
);

const initialState = {
  filters: {
    machines: [],
    locations: [],
    machineGroups: [],
    maintenanceTypes: [],
  },
  selectedFilters: {
    machines: [],
    locations: [],
    machineGroups: [],
    maintenanceTypes: [],
  },
  activeFilters: {
    machines: [],
    locations: [],
    machineGroups: [],
    maintenanceTypes: [],
  },
  // New: Advanced filter groups with AND/OR operators
  filterGroups: [],
  // Global operator for advanced filters (AND/OR)
  globalOperator: 'AND',
  // Flag to enable new filter builder UI
  useAdvancedFilters: false,
  rawMachines: [],
  filteredMachines: [],
  isLoading: false,
  error: null,
  enabled: true,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    resetNotifications: () => JSON.parse(JSON.stringify(initialState)),
    filterMachines: (state) => {
      // Start with all rawMachines
      let candidateMachines = [...state.rawMachines];

      // Apply filters only if they have selections
      // This allows for partial filtering (e.g., only machine groups selected)

      // Apply filters using wildcard-aware logic
      if (shouldApplyFilter(state.selectedFilters.locations)) {
        candidateMachines = candidateMachines.filter((machine) =>
          matchesFilter(state.selectedFilters.locations, machine.locationIds)
        );
      }

      if (shouldApplyFilter(state.selectedFilters.machineGroups)) {
        candidateMachines = candidateMachines.filter((machine) =>
          matchesFilter(state.selectedFilters.machineGroups, machine.machineGroupIds)
        );
      }

      if (shouldApplyFilter(state.selectedFilters.maintenanceTypes)) {
        candidateMachines = candidateMachines.filter((machine) =>
          matchesFilter(state.selectedFilters.maintenanceTypes, machine.maintenanceTypeIds)
        );
      }

      // Calculate active filters based on candidate machines (before machine filter)
      // This shows what's available for selection
      state.activeFilters.machines = [...new Set(candidateMachines.map((machine) => +machine.id))];
      state.activeFilters.machineGroups = [
        ...new Set(candidateMachines.flatMap((machine) => machine.machineGroupIds)),
      ];
      state.activeFilters.locations = [
        ...new Set(candidateMachines.flatMap((machine) => machine.locationIds)),
      ];
      state.activeFilters.maintenanceTypes = [
        ...new Set(candidateMachines.flatMap((machine) => machine.maintenanceTypeIds)),
      ];

      // Finally, filter by selected machines using wildcard-aware logic
      if (shouldApplyFilter(state.selectedFilters.machines)) {
        state.filteredMachines = candidateMachines.filter((machine) =>
          matchesFilter(state.selectedFilters.machines, [+machine.id])
        );
      } else {
        // If no machines are specifically selected, use all candidate machines
        state.filteredMachines = candidateMachines;
      }
    },
    toggleMachine: (state, action) => {
      // Handle wildcard toggle
      if (action.payload === '*') {
        if (state.selectedFilters.machines === '*') {
          // Switch from wildcard to empty
          state.selectedFilters.machines = [];
        } else {
          // Switch to wildcard
          state.selectedFilters.machines = '*';
        }
        return;
      }

      // Handle regular toggle - only if current state is array
      if (Array.isArray(state.selectedFilters.machines)) {
        if (state.selectedFilters.machines.includes(action.payload)) {
          state.selectedFilters.machines = state.selectedFilters.machines.filter(
            (id) => id !== action.payload
          );
        } else {
          state.selectedFilters.machines = [...state.selectedFilters.machines, action.payload];
        }
      }
    },
    toggleGroup: (state, action) => {
      // Handle wildcard toggle
      if (action.payload === '*') {
        if (state.selectedFilters.machineGroups === '*') {
          // Switch from wildcard to empty
          state.selectedFilters.machineGroups = [];
        } else {
          // Switch to wildcard
          state.selectedFilters.machineGroups = '*';
        }
        return;
      }

      // Handle regular toggle - only if current state is array
      if (Array.isArray(state.selectedFilters.machineGroups)) {
        if (state.selectedFilters.machineGroups.includes(action.payload)) {
          state.selectedFilters.machineGroups = state.selectedFilters.machineGroups.filter(
            (id) => id !== action.payload
          );
        } else {
          state.selectedFilters.machineGroups = [
            ...state.selectedFilters.machineGroups,
            action.payload,
          ];
        }
      }
    },
    toggleLocation: (state, action) => {
      // Handle wildcard toggle
      if (action.payload === '*') {
        if (state.selectedFilters.locations === '*') {
          // Switch from wildcard to empty
          state.selectedFilters.locations = [];
        } else {
          // Switch to wildcard
          state.selectedFilters.locations = '*';
        }
        return;
      }

      // Handle regular toggle - only if current state is array
      if (Array.isArray(state.selectedFilters.locations)) {
        if (state.selectedFilters.locations.includes(action.payload)) {
          state.selectedFilters.locations = state.selectedFilters.locations.filter(
            (id) => id !== action.payload
          );
        } else {
          state.selectedFilters.locations = [...state.selectedFilters.locations, action.payload];
        }
      }
    },
    toggleType: (state, action) => {
      // Handle wildcard toggle
      if (action.payload === '*') {
        if (state.selectedFilters.maintenanceTypes === '*') {
          // Switch from wildcard to empty
          state.selectedFilters.maintenanceTypes = [];
        } else {
          // Switch to wildcard
          state.selectedFilters.maintenanceTypes = '*';
        }
        return;
      }

      // Handle regular toggle - only if current state is array
      if (Array.isArray(state.selectedFilters.maintenanceTypes)) {
        if (state.selectedFilters.maintenanceTypes.includes(action.payload)) {
          state.selectedFilters.maintenanceTypes = state.selectedFilters.maintenanceTypes.filter(
            (id) => id !== action.payload
          );
        } else {
          state.selectedFilters.maintenanceTypes = [
            ...state.selectedFilters.maintenanceTypes,
            action.payload,
          ];
        }
      }
    },
    toggleEnabled: (state) => {
      state.enabled = !state.enabled;
    },
    // New: Advanced filter management
    setFilterGroups: (state, action) => {
      state.filterGroups = action.payload;
    },
    toggleAdvancedFilters: (state) => {
      state.useAdvancedFilters = !state.useAdvancedFilters;
    },
    setUseAdvancedFilters: (state, action) => {
      state.useAdvancedFilters = action.payload;
    },
    setGlobalOperator: (state, action) => {
      state.globalOperator = action.payload;
    },
    applyAdvancedFilters: (state) => {
      // Apply advanced filter groups with AND/OR logic
      if (!state.useAdvancedFilters || state.filterGroups.length === 0) {
        state.filteredMachines = [...state.rawMachines];
        return;
      }

      let resultMachines = [];

      // Process each filter group
      state.filterGroups.forEach((group, groupIndex) => {
        // Get machines that match ALL conditions within this group (AND logic within group)
        let groupMachines = [...state.rawMachines];

        group.conditions.forEach((condition) => {
          if (!condition.selectedValues || condition.selectedValues.length === 0) {
            return; // Skip empty conditions
          }

          // Filter machines based on condition type
          groupMachines = groupMachines.filter((machine) => {
            switch (condition.filterType) {
              case 'machines':
                return condition.selectedValues.includes(+machine.id);

              case 'machineGroups':
                return machine.machineGroupIds &&
                  condition.selectedValues.every((groupId) =>
                    machine.machineGroupIds.includes(groupId)
                  );

              case 'locations':
                return machine.locationIds &&
                  condition.selectedValues.every((locationId) =>
                    machine.locationIds.includes(locationId)
                  );

              case 'maintenanceTypes':
                return machine.maintenanceTypeIds &&
                  condition.selectedValues.every((typeId) =>
                    machine.maintenanceTypeIds.includes(typeId)
                  );

              default:
                return true;
            }
          });
        });

        // Combine with previous groups based on operator
        if (groupIndex === 0) {
          // First group sets the initial result
          resultMachines = groupMachines;
        } else {
          if (group.operator === 'OR') {
            // OR: Add machines from this group (union)
            const machineIds = new Set(resultMachines.map((m) => m.id));
            groupMachines.forEach((machine) => {
              if (!machineIds.has(machine.id)) {
                resultMachines.push(machine);
              }
            });
          } else {
            // AND: Keep only machines that are in both sets (intersection)
            const groupMachineIds = new Set(groupMachines.map((m) => m.id));
            resultMachines = resultMachines.filter((machine) =>
              groupMachineIds.has(machine.id)
            );
          }
        }
      });

      state.filteredMachines = resultMachines;

      // Update activeFilters based on filtered machines
      state.activeFilters.machines = [...new Set(resultMachines.map((machine) => +machine.id))];
      state.activeFilters.machineGroups = [
        ...new Set(resultMachines.flatMap((machine) => machine.machineGroupIds)),
      ];
      state.activeFilters.locations = [
        ...new Set(resultMachines.flatMap((machine) => machine.locationIds)),
      ];
      state.activeFilters.maintenanceTypes = [
        ...new Set(resultMachines.flatMap((machine) => machine.maintenanceTypeIds)),
      ];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNotifications.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      state.isLoading = false;
      state.filters = action.payload.filters;
      state.rawMachines = action.payload.rawMachines;
      state.selectedFilters = action.payload.selectedFilters;
      state.error = null;
      state.enabled = action.payload.enabled ?? true; // Fallback to true if undefined

      // Restore advanced filter groups if available
      state.filterGroups = action.payload.filterGroups || [];
      state.useAdvancedFilters = action.payload.useAdvancedFilters || false;

      // Update activeFilters to reflect wildcard selections for UI display
      state.activeFilters = {
        machines: resolveWildcardForDisplay(
          state.selectedFilters.machines,
          action.payload.filters.machines
        ),
        machineGroups: resolveWildcardForDisplay(
          state.selectedFilters.machineGroups,
          action.payload.filters.machineGroups
        ),
        locations: resolveWildcardForDisplay(
          state.selectedFilters.locations,
          action.payload.filters.locations
        ),
        maintenanceTypes: resolveWildcardForDisplay(
          state.selectedFilters.maintenanceTypes,
          action.payload.filters.maintenanceTypes
        ),
      };
    });
    builder.addCase(fetchNotifications.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    // Delete notifications
    builder.addCase(deleteNotifications.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteNotifications.fulfilled, (state) => {
      state.isLoading = false;
      // Reset to initial state after successful deletion
      state.selectedFilters = {
        machines: [],
        locations: [],
        machineGroups: [],
        maintenanceTypes: [],
      };
      state.activeFilters = {
        machines: [],
        locations: [],
        machineGroups: [],
        maintenanceTypes: [],
      };
      state.filteredMachines = [];
      state.error = null;
    });
    builder.addCase(deleteNotifications.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
  },
});

export const {
  resetNotifications,
  filterMachines,
  toggleGroup,
  toggleLocation,
  toggleMachine,
  toggleType,
  toggleEnabled,
  setFilterGroups,
  toggleAdvancedFilters,
  setUseAdvancedFilters,
  setGlobalOperator,
  applyAdvancedFilters,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;

export const selectNotifications = (state) => state.notifications.notifications;
